import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  api,
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SESSION_EXPIRED_MESSAGE,
  resetAuthSessionExpiredGuard,
} from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setLoading(false);
      toast.error(AUTH_SESSION_EXPIRED_MESSAGE);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  const fetchMe = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("fullbook_token");
    if (token) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    localStorage.setItem("fullbook_token", response.data.token);
    resetAuthSessionExpiredGuard();
    setUser(response.data.user);
    toast.success("Bentornato.");
  };

  const register = async (payload) => {
    const response = await api.post("/auth/register", payload);
    localStorage.setItem("fullbook_token", response.data.token);
    resetAuthSessionExpiredGuard();
    setUser(response.data.user);
    toast.success("Account creato.");
  };

  const logout = () => {
    localStorage.removeItem("fullbook_token");
    resetAuthSessionExpiredGuard();
    setUser(null);
    toast.success("Disconnessione effettuata.");
  };

  const isOwner = user?.role === "owner";

  const value = useMemo(
    () => ({ user, loading, isOwner, login, register, logout }),
    [user, loading, isOwner]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
